import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics } from '@/utils/analytics';

/**
 * Hook to track scroll depth and time on page
 * Automatically tracks when user scrolls to 25%, 50%, 75%, and 100% of the page
 * Also tracks total time spent on page when component unmounts
 */
export function usePageAnalytics() {
    const location = useLocation();
    const startTimeRef = useRef<number>(Date.now());
    const scrollTrackedRef = useRef<Set<number>>(new Set());
    const lastScrollTimeRef = useRef<number>(Date.now());

    useEffect(() => {
        // Reset tracking for new page
        startTimeRef.current = Date.now();
        scrollTrackedRef.current.clear();

        // Track page view (with retry if gtag not loaded yet)
        const trackPageView = () => {
            analytics.pageView(location.pathname);
        };

        // Try immediately
        trackPageView();

        // If gtag wasn't ready, retry after a short delay
        const retryTimeout = setTimeout(() => {
            trackPageView();
        }, 500);

        const handleScroll = () => {
            // Debounce scroll events
            const now = Date.now();
            if (now - lastScrollTimeRef.current < 100) return;
            lastScrollTimeRef.current = now;

            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const scrollTop = window.scrollY;

            const scrollPercentage = Math.round(
                ((scrollTop + windowHeight) / documentHeight) * 100
            );

            // Track at 25%, 50%, 75%, and 100%
            const milestones = [25, 50, 75, 100];
            milestones.forEach((milestone) => {
                if (
                    scrollPercentage >= milestone &&
                    !scrollTrackedRef.current.has(milestone)
                ) {
                    scrollTrackedRef.current.add(milestone);
                    analytics.scrollDepth(milestone);
                }
            });
        };

        // Add scroll listener
        window.addEventListener('scroll', handleScroll, { passive: true });

        // Cleanup and track time on page
        return () => {
            clearTimeout(retryTimeout);
            window.removeEventListener('scroll', handleScroll);

            const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
            if (timeSpent > 5) { // Only track if user spent more than 5 seconds
                analytics.timeOnPage(timeSpent);
            }
        };
    }, [location.pathname]);
}
